import proj4 from 'proj4';
import { transform } from 'ol/proj';
import { register } from 'ol/proj/proj4';

import { ObserverPositionDto } from '../models/observer-position-dto';

const EARTH_RADIUS_METERS = 6371000;
export const WGS84_PROJECTION = 'EPSG:4326';
export const RD_NEW_PROJECTION = 'EPSG:28992';
export const BREDA_CENTER_LON_LAT: [number, number] = [4.7683, 51.5719];

let isRdProjectionRegistered = false;

export function registerDutchRdProjection(): void {
  if (isRdProjectionRegistered) {
    return;
  }

  proj4.defs(
    RD_NEW_PROJECTION,
    '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 ' +
      '+x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.2369,50.0087,465.658,-0.406857330322398,0.350732676542563,-1.8703473836068,4.0812 ' +
      '+units=m +no_defs +type=crs',
  );

  register(proj4);
  isRdProjectionRegistered = true;
}

export function lonLatToRdCoordinate(coordinate: [number, number]): [number, number] {
  return transform(coordinate, WGS84_PROJECTION, RD_NEW_PROJECTION) as [number, number];
}

export function formatDateTimeWithLocalTimezone(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absoluteOffsetMinutes / 60));
  const offsetRemainderMinutes = pad(absoluteOffsetMinutes % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetRemainderMinutes}`;
}

export function normalizeObserverResponse(
  response: ObserverPositionDto | ObserverPositionDto[] | null | undefined,
): ObserverPositionDto[] {
  if (!response) {
    return [];
  }

  return Array.isArray(response) ? response : [response];
}

export function isValidCoordinate(
  longitude: number | null | undefined,
  latitude: number | null | undefined,
): boolean {
  return (
    typeof longitude === 'number' &&
    typeof latitude === 'number' &&
    Number.isFinite(longitude) &&
    Number.isFinite(latitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    latitude >= -90 &&
    latitude <= 90
  );
}

export function isValidBearing(bearingDegrees: number | null | undefined): bearingDegrees is number {
  return typeof bearingDegrees === 'number' && Number.isFinite(bearingDegrees);
}

export function calculateDestinationCoordinate(
  longitude: number,
  latitude: number,
  bearingDegrees: number,
  distanceMeters: number,
): [number, number] {
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const bearingRadians = toRadians(normalizeBearing(bearingDegrees));
  const latitudeRadians = toRadians(latitude);
  const longitudeRadians = toRadians(longitude);

  const destinationLatitudeRadians = Math.asin(
    Math.sin(latitudeRadians) * Math.cos(angularDistance) +
      Math.cos(latitudeRadians) * Math.sin(angularDistance) * Math.cos(bearingRadians),
  );

  const destinationLongitudeRadians =
    longitudeRadians +
    Math.atan2(
      Math.sin(bearingRadians) * Math.sin(angularDistance) * Math.cos(latitudeRadians),
      Math.cos(angularDistance) - Math.sin(latitudeRadians) * Math.sin(destinationLatitudeRadians),
    );

  const destinationLongitude = normalizeLongitude(toDegrees(destinationLongitudeRadians));
  const destinationLatitude = toDegrees(destinationLatitudeRadians);

  return [destinationLongitude, destinationLatitude];
}

function normalizeBearing(bearingDegrees: number): number {
  return ((bearingDegrees % 360) + 360) % 360;
}

function normalizeLongitude(longitude: number): number {
  return ((longitude + 540) % 360) - 180;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}
